import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n, { LANGUAGE_LABELS, SUPPORTED_LANGUAGES } from '@/i18n';
import type { SupportedLanguage } from '@/i18n';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';

const LEGAL_LINKS: { to: string; key: string }[] = [
  { to: '/impressum', key: 'footer.imprint' },
  { to: '/datenschutz', key: 'footer.privacy' },
  { to: '/agb', key: 'footer.terms' },
  { to: '/community', key: 'footer.community' },
  { to: '/widerruf', key: 'footer.withdrawal' },
];

export default function Footer() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Switch the app language immediately (i18next persists to localStorage via
  // the detector). For logged-in users, also store the preference in
  // user_settings so it follows them across devices — quietly, no toast.
  const changeLanguage = (lang: SupportedLanguage) => {
    void i18n.changeLanguage(lang);
    if (!user) return;
    // .update() silently matches 0 rows when the user has no user_settings
    // row yet — detect that via .select() and insert the row instead.
    void supabase
      .from('user_settings')
      .update({ language: lang })
      .eq('user_id', user.id)
      .select('user_id')
      .then(({ data, error }) => {
        if (error) {
          console.warn('Could not persist language setting:', error);
          return;
        }
        if (!data || data.length === 0) {
          void supabase
            .from('user_settings')
            .insert({ user_id: user.id, language: lang })
            .then(({ error: insertError }) => {
              if (insertError)
                console.warn('Could not insert language setting:', insertError);
            });
        }
      });
  };

  const activeLang = i18n.language.split('-')[0];

  return (
    <footer className="w-full border-t border-parchment-dark/30 bg-parchment-dark">
      <div className="mx-auto flex max-w-[430px] flex-col items-center px-6 py-10">
        {/* Leaf mark */}
        <img
          src="/brand/cherry-leaf-footer.svg"
          alt=""
          className="mb-5 h-8 w-auto opacity-60"
          aria-hidden="true"
        />

        {/* Work with Cherry link */}
        <div className="mb-4 flex items-center gap-3">
          <Link
            to="/work-with-cherry"
            className="font-body text-sm text-ink-muted transition-colors duration-200 hover:text-terra"
          >
            {t('footer.work_with_cherry')}
          </Link>
        </div>

        {/* Legal links */}
        <nav
          aria-label={t('footer.imprint')}
          className="mb-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1"
        >
          {LEGAL_LINKS.map(({ to, key }, i) => (
            <span key={to} className="flex items-center gap-3">
              {i > 0 && (
                <span aria-hidden="true" className="text-ink-muted/30">
                  ·
                </span>
              )}
              <Link
                to={to}
                className="font-body text-xs text-ink-muted transition-colors duration-200 hover:text-terra"
              >
                {t(key)}
              </Link>
            </span>
          ))}
        </nav>

        {/* Language switcher */}
        <label className="mb-4 flex items-center gap-2 font-body text-xs text-ink-muted">
          <span>{t('settings.language')}</span>
          <select
            value={activeLang}
            onChange={(e) => changeLanguage(e.target.value as SupportedLanguage)}
            className="rounded-md border border-parchment-dark/40 bg-parchment px-2 py-1 font-body text-xs text-ink transition-colors duration-200 hover:border-ink-muted/40 focus:outline-none focus-visible:ring-1 focus-visible:ring-green"
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {LANGUAGE_LABELS[lang]}
              </option>
            ))}
          </select>
        </label>

        {/* Tagline */}
        <p className="mb-2 font-body text-xs text-ink-muted/70">
          {t('footer.tagline')}
        </p>

        {/* Copyright */}
        <p className="font-body text-xs text-ink-muted/50">
          &copy; 2026 Cherry Books
        </p>
      </div>
    </footer>
  );
}
